import React from 'react';
import { toastr } from 'react-redux-toastr';
import { useTranslation } from 'react-i18next';
import { authService } from 'services';
import '../AttendanceSupervisor/filters/EmployeeFilter.scss';
import './CopyStoreFoldersModal.scss';

const CopyStoreFoldersModal = ({ closeModal, sourceEmployeeId }) => {
  const { t } = useTranslation();
  const [companies] = React.useState(authService.getAllCompanyTokens() || []);
  const [selectedCompanyName, setSelectedCompanyName] = React.useState(authService.currentCompanyId || (companies[0]?.company_name || ''));
  const [users, setUsers] = React.useState([]);
  const [loadingUsers, setLoadingUsers] = React.useState(false);
  const [userQuery, setUserQuery] = React.useState('');
  const [selectedUserId, setSelectedUserId] = React.useState('');
  const [isUserOpen, setIsUserOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);
  const [submitting, setSubmitting] = React.useState(false);

  const getTargetCompanyToken = React.useCallback(() => {
    return companies.find(c => c.company_name === selectedCompanyName);
  }, [companies, selectedCompanyName]);

  const fetchUsers = React.useCallback(async (q = '') => {
    const targetCompany = getTargetCompanyToken();
    if (!targetCompany) {
      setUsers([]);
      return;
    }

    setLoadingUsers(true);
    try {
      const params = new URLSearchParams({ sort: '+name,+surname', variant: 'select', page_size: '10000' });
      if (q) params.set('q', q);
      const response = await fetch(`${global.BASE_API}employees?${params.toString()}`, {
        method: 'GET',
        headers: { Authorization: `Bearer ${targetCompany.access_token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      const list = data?.data || data || [];
      // Si es la misma empresa, excluir al empleado fuente
      const filtered = selectedCompanyName === authService.currentCompanyId
        ? list.filter(u => u.id !== sourceEmployeeId)
        : list;
      setUsers(filtered);
    } catch (e) {
      console.error('Error fetching users with target token', e);
      toastr.error('Error', t('copyStoreFoldersModal.errorLoadingUsers'));
      setUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  }, [sourceEmployeeId, getTargetCompanyToken, selectedCompanyName]);

  React.useEffect(() => {
    fetchUsers('');
  }, [fetchUsers, selectedCompanyName]);

  // Cerrar dropdown al hacer clic fuera
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCompanyChange = (e) => {
    setSelectedCompanyName(e.target.value);
    setSelectedUserId('');
    setUserQuery('');
  };

  const handleSearchChange = (e) => {
    const q = e.target.value;
    setUserQuery(q);
    const local = q
      ? users.filter(u => (`${u.name} ${u.surname}`.toLowerCase().includes(q.toLowerCase()) || (u.code || '').toLowerCase().includes(q.toLowerCase())))
      : users;
    if (q && local.length < 10) {
      fetchUsers(q);
    }
  };

  const filteredUsers = React.useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return users;
    return users.filter(u => (`${u.name} ${u.surname}`.toLowerCase().includes(q) || (u.code || '').toLowerCase().includes(q)));
  }, [users, userQuery]);

  const handleSubmit = async () => {
    if (!selectedCompanyName || !selectedUserId) {
      toastr.warning(t('copyStoreFoldersModal.missingData'), t('copyStoreFoldersModal.missingDataMsg'));
      return;
    }
    const targetCompany = getTargetCompanyToken();
    if (!targetCompany) {
      toastr.error('Error', t('copyStoreFoldersModal.errorNoToken'));
      return;
    }

    setSubmitting(true);
    try {
      const url = `${global.BASE_API}store-folders/employees/${sourceEmployeeId}/copy/${selectedUserId}`;
      const resp = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${targetCompany.access_token}`,
          'Content-Type': 'application/json',
        },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toastr.success(t('common.success'), t('copyStoreFoldersModal.successCopied'));
      closeModal();
    } catch (e) {
      console.error('Error copying store folders', e);
      toastr.error('Error', t('copyStoreFoldersModal.errorCopying'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="modal-body">
        <div className="form-group">
          <label>Empresa destino</label>
          <select className="form-control" value={selectedCompanyName} onChange={handleCompanyChange}>
            {companies.map(c => (
              <option key={c.company_name} value={c.company_name}>{c.company_name}</option>
            ))}
          </select>
        </div>
        <div className="form-group employee-filter-multi copy-folders-user-select">
          <label>Usuario destino</label>
          <div className="custom-select-container" ref={dropdownRef}>
            <div
              className={`custom-select select-control ${isUserOpen ? 'open' : ''}`}
              onClick={() => setIsUserOpen(!isUserOpen)}
              style={{ cursor: 'pointer' }}
            >
              <div className="selected-items">
                {selectedUserId
                  ? (() => {
                      const u = users.find(x => String(x.id) === String(selectedUserId));
                      return (
                        <span className="selected-item">
                          {u ? `${u.name} ${u.surname}${u.code ? ` (${u.code})` : ''}` : 'Usuario seleccionado'}
                        </span>
                      );
                    })()
                  : <span className="placeholder">Seleccionar empleado de destino</span>
                }
              </div>
            </div>

            {isUserOpen && (
              <div className="dropdown-menu" onClick={(e) => e.stopPropagation()}>
                <div className="dropdown-header">
                  <input
                    type="text"
                    placeholder="Buscar empleados..."
                    value={userQuery}
                    onChange={handleSearchChange}
                    className="search-input"
                    onClick={(e) => e.stopPropagation()}
                    autoFocus
                  />
                </div>
                <div className="dropdown-options">
                  {filteredUsers.map(u => (
                    <div
                      key={u.id}
                      className={`dropdown-option ${String(selectedUserId) === String(u.id) ? 'selected' : ''}`}
                      onClick={() => { setSelectedUserId(String(u.id)); setIsUserOpen(false); }}
                    >
                      <div className="option-content">
                        <span className="employee-name">{u.name} {u.surname}</span>
                        {u.code && <span className="employee-code">({u.code})</span>}
                      </div>
                      {String(selectedUserId) === String(u.id) && <div className="checkmark">✓</div>}
                    </div>
                  ))}

                  {filteredUsers.length === 0 && (
                    <div className="no-results">No se encontraron usuarios</div>
                  )}
                </div>
              </div>
            )}
          </div>
          {loadingUsers && <small className="text-muted">{t('common.loading')}</small>}
        </div>
      </div>
      <div className="modal-footer">
        <button type="button" className="btn btn-secondary" onClick={closeModal} disabled={submitting}>{t('common.cancel')}</button>
        <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={submitting}>
          {t('copyStoreFoldersModal.copyAllFolders')}
        </button>
      </div>
    </div>
  );
};

export default CopyStoreFoldersModal;


